/**
 * The composer's `/` menu: name a skill for the next message (RFC 0283 S5).
 *
 * ALL FOUR PLACES (RFC 0334 3g): the project's, the World's, each installed
 * app's and Mod's own, in that order, each marked with where it lives and an app
 * skill with its app's tile. A query matches the title or the id, so `/weekly`
 * finds Weekly's skill by the name the agent knows it by.
 *
 * WHY A PICKER AND NOT A HINT. The catalog is already in the agent's system
 * prompt, so the model can choose a skill on its own. `/` exists for when you
 * do not want it to choose: the skill you name is put in front of it as
 * instructions for that turn, before it reads your message. Naming one is
 * therefore an instruction, not a suggestion, which is why this writes ids onto
 * the run rather than words into the message.
 *
 * OPENS ON `/` AT THE START OF AN EMPTY DRAFT, not anywhere in the text. A
 * slash mid-sentence is a path, a fraction, or a date, and a menu that appeared
 * over "and/or" would be worse than no menu.
 */

import type { SkillScope, WorldSkillRef } from '@mod/core/model'
import { useEffect, useState } from 'react'
import { AppIcon } from '../../services/apps/AppIcon'
import { SCOPE_LABEL } from '../../services/skills/skillCatalog'
import { useOpensDown } from './RefPicker'

/** A skill `/` can name. A bare World ref still works: no scope, no mark. */
export type PickableSkill = WorldSkillRef & {
  scope?: SkillScope
  appId?: string
}

export function SkillPicker({
  skills,
  query,
  onPick,
  onCreate,
  onDismiss,
}: {
  skills: PickableSkill[]
  /** What was typed after the slash, filtering by title or id. */
  query: string
  onPick: (skill: PickableSkill) => void
  /**
   * Write a new skill with this title and open it for editing.
   *
   * HERE, because `/` is where you find out the World has none. Sending you to
   * the cli or asking an agent at the moment you discover the gap is how a
   * feature stays permanently empty: the first skill has to be creatable from
   * the place that told you there were none.
   */
  onCreate?: (title: string) => void
  onDismiss: () => void
}) {
  const q = query.trim().toLowerCase()
  const matches = q
    ? skills.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.skillId.toLowerCase().includes(q),
      )
    : skills
  // Offered only once there is a name to give it. A New row on an empty query
  // would create "Untitled", and a skill is chosen by its title.
  const canCreate = Boolean(onCreate && query.trim())
  const [index, setIndex] = useState(0)

  // Typing narrows the list under a selection that may no longer exist, so the
  // highlight returns to the top rather than pointing past the end.
  useEffect(() => setIndex(0), [])

  const active = Math.min(index, Math.max(matches.length - 1, 0))
  const [ref, down] = useOpensDown<HTMLDivElement>()
  const side = down ? 'top-full mt-2' : 'bottom-full mb-2'

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (matches.length === 0) {
        // Enter on a name that matches nothing CREATES it, so naming a skill
        // that does not exist yet is one keystroke rather than a dead end.
        if (canCreate && e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          onCreate?.(query.trim())
        } else if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          onDismiss()
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setIndex((i) => (i + 1) % matches.length)
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setIndex((i) => (i - 1 + matches.length) % matches.length)
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        // Captured before the composer's own Enter handler, which would
        // otherwise send a message consisting of "/writ".
        e.preventDefault()
        e.stopPropagation()
        const picked = matches[active]
        if (picked) onPick(picked)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onDismiss()
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [matches, active, onPick, onDismiss, canCreate, onCreate, query])

  if (matches.length === 0) {
    return (
      <div
        ref={ref}
        className={`absolute ${side} left-0 z-20 w-full max-w-md rounded border border-border bg-background shadow-lg`}
      >
        <div className="p-3 text-muted-foreground text-xs">
          {skills.length === 0
            ? canCreate
              ? 'No skills here yet.'
              : 'No skills here yet. Type a name to write one.'
            : `No skill matches "${query}".`}
        </div>
        {canCreate && (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              onCreate?.(query.trim())
            }}
            className="block w-full border-border border-t px-3 py-2 text-left"
          >
            <div className="text-foreground text-sm">
              New skill "{query.trim()}"
            </div>
            <div className="text-muted-foreground text-xs">
              Writes it and opens it for editing
            </div>
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={`absolute ${side} left-0 z-20 max-h-64 w-full max-w-md overflow-y-auto rounded border border-border bg-background shadow-lg`}
    >
      {matches.map((skill, i) => (
        <button
          key={skill.skillId}
          type="button"
          // Mouse down, not click: the composer's textarea would lose focus on
          // blur first and close the menu before a click ever landed.
          onMouseDown={(e) => {
            e.preventDefault()
            onPick(skill)
          }}
          onMouseEnter={() => setIndex(i)}
          className={`block w-full px-3 py-2 text-left ${
            i === active ? 'bg-muted' : ''
          }`}
        >
          <div className="flex items-center gap-2 text-foreground text-sm">
            {skill.appId && <AppIcon appId={skill.appId} size={14} />}
            <span className="min-w-0 flex-1 truncate">{skill.title}</span>
            {skill.scope && (
              <span className="shrink-0 text-[10px] text-muted-foreground uppercase tracking-wide">
                {SCOPE_LABEL[skill.scope]}
              </span>
            )}
          </div>
          <div className="truncate text-muted-foreground text-xs">
            {skill.description}
          </div>
        </button>
      ))}
    </div>
  )
}
